import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Globe2,
  LockKeyhole,
  ShieldCheck,
  Target,
  UsersRound,
} from "lucide-react";
import Button from "../components/Button";
import CTA from "../components/public/CTA";
import FAQSection from "../components/public/FAQSection";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import ServiceCard from "../components/ServiceCard";
import TestimonialStrip from "../components/public/TestimonialStrip";
import { proofPoints } from "../data/siteContent";
import { services } from "../data/services";
import { CUSTOMER_PORTAL_URL } from "../lib/portal";

const heroServices = [
  {
    label: "Automobile services",
    caption: "Unbeatable market prices for premium cars",
    slug: "automobile-services",
    video: "https://cdn.coverr.co/videos/coverr-driving-a-mercedes-6097/1080p.mp4",
    poster: "https://cdn.coverr.co/videos/coverr-driving-a-mercedes-6097/thumbnail?width=1920",
  },
  {
    label: "Logistics",
    caption: "Reliable logistics services to get you from point A to B",
    slug: "logistics",
    video: "https://videos.pexels.com/video-files/8992559/8992559-hd_1920_1080_30fps.mp4",
    poster: "https://images.pexels.com/photos/7018655/pexels-photo-7018655.jpeg?auto=compress&cs=tinysrgb&w=1920",
  },
  {
    label: "Clearing services",
    caption: "Hassle-free shipping and customs clearance services",
    slug: "clearing-and-forwarding",
    video: "https://cdn.coverr.co/videos/coverr-forklift-in-a-factory-2015/1080p.mp4",
    poster: "https://cdn.coverr.co/videos/coverr-forklift-in-a-factory-2015/thumbnail?width=1920",
  },
  {
    label: "Property services",
    caption: "Real estate investment with ease",
    slug: "real-estate-services",
    video: "https://cdn.coverr.co/videos/coverr-villa-with-a-swimming-pool-6618/1080p.mp4",
    poster: "https://cdn.coverr.co/videos/coverr-villa-with-a-swimming-pool-6618/thumbnail?width=1920",
  },
  {
    label: "Concierge services",
    caption: "Your trusted concierge partner",
    slug: "concierge-services",
    video: "https://videos.pexels.com/video-files/7820515/7820515-hd_1920_1080_25fps.mp4",
    poster: "https://images.pexels.com/videos/7820515/pictures/preview-3.jpg",
  },
  {
    label: "Procurement",
    caption: "End-to-end procurement services that you can rely on",
    slug: "procurement-management",
    video: "https://cdn.coverr.co/videos/coverr-putting-clothes-in-the-warehouse-4939/1080p.mp4",
    poster: "https://cdn.coverr.co/videos/coverr-putting-clothes-in-the-warehouse-4939/thumbnail?width=1920",
  },
  {
    label: "Compliance",
    caption: "Seamless regulatory compliance services",
    slug: "regulatory-compliance",
    video: "https://cdn.coverr.co/videos/coverr-conference-meeting-9389/1080p.mp4",
    poster: "https://cdn.coverr.co/videos/coverr-conference-meeting-9389/thumbnail?width=1920",
  },
];

const proofIcons = [UsersRound, Target, Globe2];

export function Home() {
  const [heroServiceIndex, setHeroServiceIndex] = useState(0);
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setHeroServiceIndex(
        (current) => (current + 1) % heroServices.length,
      );
    }, 7000);
    return () => window.clearInterval(intervalId);
  }, []);
  const activeHeroService = heroServices[heroServiceIndex];
  return (
    <>
      <section className="home-hero relative overflow-hidden bg-ink text-white">
        <div className="hero-video-shell absolute inset-0" aria-hidden="true">
          <video
            key={activeHeroService.video}
            className="hero-video absolute inset-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={activeHeroService.poster}
          >
            <source src={activeHeroService.video} type="video/mp4" />
          </video>
          <div className="hero-video-overlay absolute inset-0" />
        </div>
        <div className="tech-grid absolute inset-0 opacity-10" />
        <div className="absolute -left-40 top-24 h-[520px] w-[520px] rounded-full bg-ocean/10 blur-[130px]" />
        <div className="absolute -right-48 bottom-[-100px] h-[520px] w-[520px] rounded-full bg-bridge/[.08] blur-[130px]" />
        <div className="container relative flex min-h-[100dvh] items-center justify-center pb-16 pt-32 sm:pb-20 sm:pt-36">
          <Reveal className="relative z-10 mx-auto flex min-w-0 w-full max-w-5xl flex-col items-center justify-center px-1 text-center">
            <p className="eyebrow max-w-full text-balance leading-5 text-cyan">
              <span className="mr-3 inline-block h-px w-10 align-middle bg-cyan" />
              Your trusted execution partner in Nigeria
            </p>
            <h1 className="hero-copy-title mt-7 w-full max-w-6xl text-balance font-display text-[2rem] font-extrabold leading-[1.04] tracking-[-.05em] min-[390px]:text-[2.15rem] sm:text-[3.2rem] lg:text-[4.25rem] xl:text-[4.8rem]">
              <Link
                to={`/services/${activeHeroService.slug}`}
                key={activeHeroService.label}
                aria-live="polite"
                className="block animate-slide-up break-words text-inherit no-underline hover:text-inherit focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-4 focus-visible:ring-offset-ink"
              >
                {activeHeroService.caption}.
              </Link>
            </h1>
            <div className="mt-9 flex w-full flex-nowrap justify-center gap-2 sm:gap-3">
              <Button href={`${CUSTOMER_PORTAL_URL}/signup`} className="min-w-0 whitespace-nowrap px-4 sm:px-5">
                Get started
              </Button>
              <Button
                to="/how-it-works"
                variant="light"
                className="min-w-0 whitespace-nowrap px-4 sm:px-5"
              >
                See how it works
              </Button>
            </div>
            <div className="mt-12 flex flex-wrap justify-center gap-6 text-sm text-slate-200 lg:gap-8">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck size={17} className="text-bridge" />
                Accountable operations
              </span>
              <span className="inline-flex items-center gap-2">
                <LockKeyhole size={17} className="text-bridge" />
                Secure milestones
              </span>
            </div>
            <div className="mt-10 flex items-center justify-center gap-2" aria-label={`Service ${heroServiceIndex + 1} of ${heroServices.length}`}>
              {heroServices.map((service, index) => (
                <button
                  key={service.label}
                  type="button"
                  aria-label={`Show ${service.label}`}
                  aria-pressed={index === heroServiceIndex}
                  onClick={() => setHeroServiceIndex(index)}
                  className={`h-1 appearance-none rounded-full border-0 p-0 transition-all duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-ink ${index === heroServiceIndex ? "w-10 bg-bridge shadow-[0_0_14px_rgba(162,217,75,.8)]" : "w-4 bg-white/35 hover:bg-white/60"}`}
                />
              ))}
            </div>
          </Reveal>
        </div>
      </section>
      <section className="border-b border-ink/10 bg-white">
        <div className="container grid gap-8 py-8 sm:grid-cols-3">
          {proofPoints.map(([, title, copy], index) => {
            const Icon = proofIcons[index];
            return (
              <div key={title} className="flex gap-4">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-ocean/10 text-ocean">
                  <Icon size={28} strokeWidth={2.2} aria-hidden="true" />
                </span>
                <div>
                  <p className="font-display font-bold text-ink">{title}</p>
                  <p className="mt-1 text-sm leading-6 text-slate">{copy}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <section className="section-space relative overflow-hidden bg-[#111810]" id="services">
        <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/38941376/pexels-photo-38941376.jpeg?auto=compress&cs=tinysrgb&w=2000')] bg-cover bg-center opacity-30" aria-hidden="true" />
        <div className="absolute inset-0 bg-[linear-gradient(112deg,rgba(8,16,13,.98)_4%,rgba(15,29,20,.91)_54%,rgba(32,36,22,.84)_100%)]" aria-hidden="true" />
        <div className="absolute -right-32 top-10 h-96 w-96 rounded-full bg-bridge/10 blur-[110px]" aria-hidden="true" />
        <div className="container relative">
          <SectionHeading
            eyebrow="What we move"
            title="Practical services. Thoughtful execution."
            copy="Tell us what needs to happen in Nigeria. We will bring the structure, local context and operational ownership to move it forward."
            light
          />
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {services.map((service, index) => (
              <ServiceCard key={service.slug} service={service} index={index} dark />
            ))}
          </div>
          <div className="mt-10 text-center">
            <Button to="/services" variant="light">
              Explore all service lines
            </Button>
          </div>
        </div>
      </section>
      <section className="section-space bg-white" id="managed-work">
        <div className="container grid items-center gap-12 lg:grid-cols-[.9fr_1.1fr]">
          <Reveal>
            <SectionHeading
              eyebrow="Managed from request to handover"
              title="You stay informed. We keep the work moving."
              copy="The Bridge turns your requirement into coordinated local action. Whether it is a vehicle inspection, property viewing, shipment, procurement brief or compliance task, one accountable team manages the details and keeps the scope, approvals, milestones and supporting evidence visible to you."
            />
            <Button to="/about" className="mt-8">
              See how we deliver
            </Button>
          </Reveal>
          <Reveal delay={100} className="grid min-h-[520px] grid-cols-2 gap-4">
            <figure className="group relative col-span-2 overflow-hidden rounded-[2rem] sm:col-span-1 sm:row-span-2">
              <img src="https://images.pexels.com/photos/7540622/pexels-photo-7540622.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="A Black technician completing detailed work on a vehicle" className="h-full min-h-[360px] w-full object-cover transition duration-700 group-hover:scale-105" />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink via-ink/75 to-transparent p-6 pt-20 text-white"><span className="text-xs font-extrabold uppercase tracking-[.18em] text-cyan">Automobile services</span><strong className="mt-2 block font-display text-xl">Inspection before commitment.</strong></figcaption>
            </figure>
            <figure className="group relative overflow-hidden rounded-[2rem]">
              <img src="https://images.pexels.com/photos/7489096/pexels-photo-7489096.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="A property professional guiding clients through an apartment viewing" className="h-full min-h-[250px] w-full object-cover transition duration-700 group-hover:scale-105" />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/95 to-transparent p-5 pt-16 text-sm font-bold text-white">Property viewed and verified on your behalf.</figcaption>
            </figure>
            <figure className="group relative overflow-hidden rounded-[2rem]">
              <img src="https://images.pexels.com/photos/7018655/pexels-photo-7018655.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="A Black warehouse professional organizing and verifying inventory" className="h-full min-h-[250px] w-full object-cover transition duration-700 group-hover:scale-105" />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/95 to-transparent p-5 pt-16 text-sm font-bold text-white">Procurement tracked beyond the purchase order.</figcaption>
            </figure>
          </Reveal>
        </div>
      </section>
      <TestimonialStrip />
      <FAQSection />
      <CTA />
    </>
  );
}
